<template>
  <v-app :theme="'opusTheme'">
    <!-- Top App Bar -->
    <v-app-bar color="primary" elevation="0" height="56">
      <v-app-bar-nav-icon color="white" @click="railDrawer = !railDrawer" />

      <div class="d-flex align-center">
        <img :src="opusLogo" alt="Opus" height="28" class="mr-3" />
        <div class="d-flex flex-column" style="line-height: 1.2;">
          <span class="text-white text-body-1 font-weight-medium">Reference Architecture Template</span>
        </div>
      </div>

      <v-spacer />

      <!-- User menu -->
      <template v-if="authenticated">
        <v-tooltip location="bottom" :text="me ? `Roles: ${me.roles.join(', ') || 'none'}` : 'Role not loaded'">
          <template #activator="{ props: tipProps }">
            <v-chip
              v-bind="tipProps"
              size="small"
              variant="flat"
              class="mr-3 font-weight-medium"
              :color="roleColor"
              style="color: white;"
            >
              {{ roleLabel }}
            </v-chip>
          </template>
        </v-tooltip>
        <span class="text-white text-body-2 mr-1 d-flex align-center">
          <v-icon size="18" class="mr-1">mdi-account-circle</v-icon>
          {{ userName }}
        </span>
        <v-btn icon="mdi-logout" variant="text" color="white" size="small" @click="handleLogout" />
      </template>
      <template v-else-if="authReady">
        <v-btn variant="outlined" color="white" size="small" @click="handleLogin">
          <v-icon start size="small">mdi-microsoft</v-icon>
          Sign In
        </v-btn>
      </template>

      <v-btn icon="mdi-help-circle-outline" variant="text" color="white" size="small" @click="helpDialog = true" />
    </v-app-bar>

    <!-- Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawerOpen"
      :rail="railDrawer"
      permanent
      color="#1e293b"
    >
      <v-list nav density="compact" class="mt-2">
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          :to="item.to"
          :value="item.to"
          active-class="sidebar-active"
          base-color="white"
          rounded="lg"
          class="mb-1"
        />
      </v-list>

      <template #append>
        <v-divider color="rgba(255,255,255,0.1)" />
        <v-list nav density="compact">
          <v-list-item
            :prepend-icon="railDrawer ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            :title="railDrawer ? 'Expand' : 'Collapse'"
            @click="railDrawer = !railDrawer"
            base-color="white"
            rounded="lg"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main>
      <v-container fluid class="pa-6">
        <template v-if="!authReady">
          <div class="d-flex justify-center align-center" style="height: 60vh;">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>
        </template>
        <template v-else-if="!authenticated">
          <div class="d-flex flex-column justify-center align-center" style="height: 60vh;">
            <v-icon icon="mdi-shield-lock" size="64" color="primary" class="mb-4" />
            <h2 class="text-h5 font-weight-medium mb-2">Sign in to continue</h2>
            <p class="text-body-2 text-medium-emphasis mb-6">Use your Opus credentials to access the Reference Architecture Template.</p>
            <v-btn color="primary" size="large" @click="handleLogin" prepend-icon="mdi-microsoft">
              Sign in with Microsoft
            </v-btn>
          </div>
        </template>
        <template v-else>
          <router-view />
        </template>
      </v-container>

      <!-- Version footer -->
      <div class="version-footer text-caption" :title="`Built ${buildTime}`">
        v{{ appVersion }} · {{ gitSha }}
      </div>
    </v-main>

    <!-- Help Dialog -->
    <v-dialog v-model="helpDialog" max-width="680" scrollable>
      <v-card rounded="lg">
        <v-card-title class="pa-5 pb-3 d-flex align-center">
          <v-icon icon="mdi-help-circle" color="primary" class="mr-2" />
          About Opus RAT
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="helpDialog = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5" style="max-height: 70vh;">
          <p class="text-body-1 mb-4">
            The <strong>Reference Architecture Template (RAT)</strong> is Opus Inspection's internal tool for
            defining, managing, and governing the architecture of our programs. It provides a
            standardized, data-driven template system that ensures consistency and compliance across all programs.
          </p>

          <v-alert type="info" variant="tonal" density="compact" class="mb-5" icon="mdi-lightbulb">
            Every inspection program has hundreds of architectural decisions — from cloud infrastructure to
            field hardware. RAT captures these decisions in a structured format so they can be reviewed,
            compared, and governed.
          </v-alert>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon icon="mdi-file-tree" size="20" color="primary" class="mr-1" />
            Template
          </h3>
          <p class="text-body-2 mb-4">
            The <strong>Template</strong> page displays the current published reference architecture template — the
            master list of all domains, fields, and allowed values that define what an inspection program's
            architecture should look like. Use this page to browse the full template structure, understand what
            fields are available, see default values, and identify which fields require justification if you
            deviate from the standard.
          </p>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon icon="mdi-cog" size="20" color="primary" class="mr-1" />
            Template Admin
          </h3>
          <p class="text-body-2 mb-4">
            The <strong>Template Admin</strong> page is where users manage the template itself. You can
            create a new draft version of the template, add or modify domains and fields, update allowed values,
            and publish new template versions. When a new version is published, existing programs remain pinned
            to their original version — they can be upgraded to the new version when ready. This ensures changes
            to the standard don't disrupt programs already in progress.
          </p>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon icon="mdi-folder-multiple" size="20" color="primary" class="mr-1" />
            Programs
          </h3>
          <p class="text-body-2 mb-4">
            The <strong>Programs</strong> page is where you create and manage individual inspection program
            architectures. Each program (e.g., "Virginia Inspection Program 2027") captures specific architectural decisions
            against the template. When you open a program, you'll see all template fields organized by domain.
            Fill in your program's values — any field where your value differs from the template default is
            flagged as a <strong>deviation</strong>. Fields marked with a warning icon require a written
            justification for why you're deviating from the standard.
          </p>

          <v-divider class="mb-4" />

          <h3 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon icon="mdi-frequently-asked-questions" size="20" color="primary" class="mr-1" />
            Getting Started
          </h3>
          <v-list density="compact" class="pa-0">
            <v-list-item prepend-icon="mdi-numeric-1-circle" class="px-0">
              <v-list-item-title class="text-body-2">Browse the <strong>Template</strong> to understand the available domains and fields.</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-numeric-2-circle" class="px-0">
              <v-list-item-title class="text-body-2">Go to <strong>Programs</strong> and click <strong>New Program</strong> to create one.</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-numeric-3-circle" class="px-0">
              <v-list-item-title class="text-body-2">Open your program and fill in architecture values section by section.</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-numeric-4-circle" class="px-0">
              <v-list-item-title class="text-body-2">Review any <strong>deviations</strong> and provide justifications where required.</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn color="primary" variant="flat" class="text-white" @click="helpDialog = false">Got It</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import opusLogo from '/opus-logo.png?url'
import { initAuth, isAuthenticated, getUserName, login, logout } from './auth/msal'
import { useCurrentUser } from './composables/useCurrentUser'

const drawerOpen = ref(true)
const railDrawer = ref(false)
const authenticated = ref(false)
const authReady = ref(false)
const userName = ref('')
const helpDialog = ref(false)

const { me, roleLabel, roleColor, loadCurrentUser } = useCurrentUser()

const appVersion = __APP_VERSION__
const gitSha = __GIT_SHA__
const buildTime = __BUILD_TIME__

onMounted(async () => {
  await initAuth()
  authenticated.value = isAuthenticated()
  userName.value = getUserName()
  authReady.value = true
  if (authenticated.value) {
    await loadCurrentUser()
  }
})

async function handleLogin() {
  await login()
}

async function handleLogout() {
  await logout()
}

const navItems = computed(() => {
  const items = [
    { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' },
    { title: 'Template', icon: 'mdi-file-tree', to: '/template' },
    { title: 'Template Admin', icon: 'mdi-cog', to: '/template/admin' },
    { title: 'Programs', icon: 'mdi-folder-multiple', to: '/programs' },
    { title: 'Audit Trail', icon: 'mdi-history', to: '/audit' },
  ]
  // Hide admin-only pages from users without the permission.
  // Until /v1/me loads (me is null), keep items visible — the API enforces
  // access regardless; this is purely cosmetic.
  if (me.value && !me.value.permissions.manageTemplate) {
    return items.filter(i => i.to !== '/template/admin' && i.to !== '/audit')
  }
  return items
})
</script>

<style>
/* Sidebar active item — high contrast white on lighter bg */
.sidebar-active {
  background-color: rgba(255, 255, 255, 0.12) !important;
  color: #ffffff !important;
}
.sidebar-active .v-list-item__prepend .v-icon {
  color: #ffffff !important;
}

/* Ensure buttons with primary color have white text */
.v-btn--variant-flat.bg-primary,
.v-btn--variant-flat.v-btn--color-primary {
  color: #ffffff !important;
}

/* Version footer — fixed bottom-right, unobtrusive */
.version-footer {
  position: fixed;
  bottom: 6px;
  right: 12px;
  color: rgba(0, 0, 0, 0.35);
  user-select: text;
  z-index: 1;
  pointer-events: auto;
}
</style>
